import React, { useContext, useRef, useState } from 'react';
import { AuthContext } from 'context/auth';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  Divider,
  useToast,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Tag,
  InputGroup,
  InputRightElement,
  useClipboard,
  Radio,
  RadioGroup,
  Stack,
  Badge,
  Wrap,
  HStack,
  Center,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';

export default function FileNameGenerator(props) {
  const { user } = useContext(AuthContext);

  const toast = useToast();
  const [fileNameOutput, setFileNameOutput] = useState('');
  const [RD, setRD] = useState(false);
  const [subsystemDisabled, setSubsystemDisabled] = useState(true);
  const [groupDisabled, setGroupDisabled] = useState(true);
  const { onCopy, value, setValue, hasCopied } = useClipboard('');
  const inputValues = useRef({
    year: new Date().getFullYear().toString().slice(-2),
    category: null,
    subsystem: null,
    name: null,
    revision: null,
    group: '1',
    assemblypart: null,
  });

  const handleFileNameOutput = () => {
    // 1796-PP-QQ-XXX-Y-Z-NAME
    // PP= Year
    // QQ= Category  Comp Robot (CR), Test Robot(TR), Prototype Concepts (P)
    // XXX= Subsystem
    // Y= Group #
    // Z= A or P Assembly or Part
    // NAME= 8 character limit. All caps. No special characters- letters only

    let YEAR = inputValues.current.year.toString().slice(-2);
    let CATEGORY = inputValues.current.category;
    let SUBSYSTEM = inputValues.current.subsystem;
    let GROUP = inputValues.current.group;
    let ASSEMBLYPART = inputValues.current.assemblypart;
    let NAME =
      inputValues.current.name && inputValues.current.name.toUpperCase();
    let fileName = '';
    // console.log(YEAR, CATEGORY, SUBSYSTEM, GROUP, ASSEMBLYPART, NAME);
    if (
      YEAR &&
      CATEGORY &&
      SUBSYSTEM &&
      NAME &&
      GROUP &&
      ASSEMBLYPART &&
      CATEGORY === 'PC'
    ) {
      fileName = `1796-${YEAR}-${CATEGORY}-${SUBSYSTEM}${GROUP}-${ASSEMBLYPART}-${NAME}`;
    } else if (
      YEAR &&
      CATEGORY &&
      SUBSYSTEM &&
      NAME &&
      ASSEMBLYPART &&
      CATEGORY !== 'PC'
    ) {
      fileName = `1796-${YEAR}-${CATEGORY}-${SUBSYSTEM}-${ASSEMBLYPART}-${NAME}`;
    } else {
      fileName = '';
    }
    setFileNameOutput(fileName);
    setValue(fileName);
  };

  const handleCategoryOnChange = e => {
    if (
      (inputValues.current.category !== 'RD' && e.value === 'RD') ||
      (inputValues.current.category === 'RD' && e.value !== 'RD') ||
      (inputValues.current.category !== 'PT' && e.value === 'PT') ||
      (inputValues.current.category === 'PT' && e.value !== 'PT')
    ) {
      inputValues.current.subsystem = null;
    }
    if (e.value === 'PC') {
      setGroupDisabled(false);
    } else {
      setGroupDisabled(true);
    }
    inputValues.current.category = e.value;

    setSubsystemDisabled(false);
    setRD(e.value !== 'RD' && e.value !== 'PT');
    handleFileNameOutput();
  };

  return (
    <>
      <Box
        margin={'0 auto'}
        mt={'1em'}
        width={{ base: '85%', md: '70%', lg: '50%' }}
      >
        <Tag backgroundColor={'white'}>
          <Breadcrumb separator={<ChevronRightIcon color="gray.500" />}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to={`/`}>
                Home
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink>File Name Generator</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        </Tag>
      </Box>
      <Box
        margin={'1em auto'}
        width={{ base: '85%', md: '70%', lg: '50%' }}
        p={4}
        borderWidth="1px"
        rounded="lg"
        shadow="1px 1px 3px rgba(0,0,0,0.3)"
        bgColor={'#fff'}
      >
        <Heading mb={2} as="h1" size="lg" textAlign="center">
          File Name Generator
        </Heading>
        <Divider />
        <form autoComplete="off">
          <FormControl isRequired mt={2}>
            <FormLabel>Year</FormLabel>
            <NumberInput
              name="year"
              onChange={value => {
                inputValues.current.year = value;
                handleFileNameOutput();
              }}
              size="md"
              defaultValue={new Date().getFullYear()}
              min={new Date().getFullYear() - 1}
              max={new Date().getFullYear() + 1}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
          <FormControl zIndex={999} isRequired mt={2}>
            <FormLabel>Category</FormLabel>
            <Select
              onChange={handleCategoryOnChange}
              placeholder="Select Category..."
              required
              name="category"
              size="md"
              options={[
                {
                  label: 'Competition Robot (CR)',
                  value: 'CR',
                },
                {
                  label: 'Test Robot (TR)',
                  value: 'TR',
                },
                {
                  label: 'Prototype Concepts (PC)',
                  value: 'PC',
                },
                {
                  label: 'Research & Development (RD)',
                  value: 'RD',
                },
                {
                  label: 'Outreach Robot (OR)',
                  value: 'OR',
                },
                {
                  label: 'Pit (PT)',
                  value: 'PT',
                },
              ]}
            />
          </FormControl>
          <FormControl zIndex={998} isRequired mt={2}>
            <FormLabel>Subsystem</FormLabel>
            {RD ? (
              <Select
                onChange={e => {
                  inputValues.current.subsystem = e.value;
                  handleFileNameOutput();
                }}
                placeholder="Select Subsystem..."
                required
                name="subsystem"
                size="md"
                options={[
                  {
                    label: 'Chassis (CHS)',
                    value: 'CHS',
                  },
                  {
                    label: 'Climb (CLM)',
                    value: 'CLM',
                  },
                  {
                    label: 'Drivetrain (DVT)',
                    value: 'DVT',
                  },
                  {
                    label: 'Elevator (ELV)',
                    value: 'ELV',
                  },
                  {
                    label: 'Feeder (FDR)',
                    value: 'FDR',
                  },
                  {
                    label: 'Hopper (HPR)',
                    value: 'HPR',
                  },
                  {
                    label: 'Intake (INT)',
                    value: 'INT',
                  },
                  {
                    label: 'Shooter (SHR)',
                    value: 'SHR',
                  },
                  {
                    label: 'Scoring Delivery (SD)',
                    value: 'SD',
                  },
                  {
                    label: 'Miscellaneous (MIS)',
                    value: 'MIS',
                  },
                ]}
              />
            ) : (
              <Input
                disabled={subsystemDisabled}
                onChange={e => {
                  if (e.target.value.length >= 3) {
                    e.target.value = e.target.value.substring(0, 3);
                  }
                  e.target.value = e.target.value
                    .replace(/[^A-Za-z0-9]/g, '')
                    .toUpperCase();
                  inputValues.current.subsystem = e.target.value;
                  handleFileNameOutput();
                }}
                required
                name="subsystem"
                size="md"
              ></Input>
            )}
          </FormControl>
          <FormControl isRequired={!groupDisabled} mt={2}>
            <FormLabel>Group</FormLabel>
            <NumberInput
              isDisabled={groupDisabled}
              name="group"
              onChange={value => {
                inputValues.current.group = value;
                handleFileNameOutput();
              }}
              defaultValue={1}
              min={1}
              max={30}
              size="md"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
          <FormControl isRequired mt={2}>
            <HStack>
              <FormLabel>Part Name</FormLabel>
              <Tag>20 Character Limit. No Spaces. No Special Characters.</Tag>
            </HStack>
            <Input
              onChange={e => {
                if (e.target.value.length >= 20) {
                  e.target.value = e.target.value.substring(0, 20);
                }
                e.target.value = e.target.value
                  .replace(/[^A-Za-z0-9]/g, '')
                  .toUpperCase();
                inputValues.current.name = e.target.value;
                handleFileNameOutput();
              }}
              required
              name="name"
              size="md"
            ></Input>
          </FormControl>
          <FormControl zIndex={997} isRequired mt={2}>
            <FormLabel>File Type</FormLabel>
            <RadioGroup
              onChange={e => {
                inputValues.current.assemblypart = e;
                handleFileNameOutput();
              }}
            >
              <Stack spacing={5} direction="row">
                <Radio colorScheme="red" value="A">
                  Assembly (A)
                </Radio>
                <Radio colorScheme="green" value="P">
                  Part (P)
                </Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
          <Divider mt={4} />
          <FormControl mt={2}>
            <FormLabel>Generated File Name:</FormLabel>
            <InputGroup size="md">
              <Input pr="4.5rem" type="text" value={fileNameOutput} readOnly />
              <InputRightElement width="4.5rem">
                <Button onClick={onCopy} h="1.75rem" size="sm">
                  {hasCopied ? 'Copied!' : 'Copy'}
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>
        </form>
      </Box>
    </>
  );
}
